import { formatCurrency } from '@/shared/utils/currencyFormatter';
import {
  hasBenefitCapitalOptions,
  type CapitalGlobalCoverageCatalogItem,
} from './coverages-catalog';

/** `RelationType` do InsureMO: a cobertura só existe com a pareada ligada. */
export const COVERAGE_RELATION_DEPENDENT_ON = '1';
/** `RelationType` do InsureMO: as duas coberturas não podem coexistir. */
export const COVERAGE_RELATION_EXCLUSIVE_WITH = '2';

export type CoverageRelations = {
  /** id → ids das irmãs mutuamente exclusivas (não inclui ela mesma). */
  exclusiveSiblings: Map<string, string[]>;
  /** id da dependente → id da cobertura de que ela depende. */
  dependsOn: Map<string, string>;
  /** id da cobertura → ids das que dependem dela. */
  dependents: Map<string, string[]>;
};

const EMPTY: CoverageRelations = {
  exclusiveSiblings: new Map(),
  dependsOn: new Map(),
  dependents: new Map(),
};

/**
 * Deriva as relações entre coberturas a partir do que o produto declara em
 * `RelationType` + `ReferenceCoverageCode`.
 *
 * **Exclusividade é de GRUPO, não de par.** O produto declara arestas dirigidas
 * (A aponta para o pai B), mas no 930015 os três auxílios funeral formam um
 * anel — 9300025 → 9300026 → 9300027 → 9300025 — e o mesmo vale para o trio
 * acidental (8200020 → 8200021 → 8200022). Tratar par a par perderia a
 * transitividade e permitiria deixar duas do mesmo anel ligadas ao mesmo tempo.
 * Por isso as arestas viram componentes conexos (união-busca, aresta não
 * dirigida): o grupo inteiro é "escolha no máximo uma".
 *
 * A cobertura pareada vem de `ParentCoverageCode` — o mesmo campo que o PME
 * usa —, com fallback em `ReferenceCoverageCode` para versões do catálogo que
 * declaravam a relação na base proporcional (as assistências-cobertura, ex.:
 * Assistência Cesta Básica, só trazem o parent).
 */
export function buildCoverageRelations(
  catalog: CapitalGlobalCoverageCatalogItem[]
): CoverageRelations {
  if (!catalog.length) return EMPTY;

  const idByCode = new Map<string, string>();
  for (const item of catalog) {
    if (item.code) idByCode.set(item.code, item.id);
  }

  // ─── Exclusividade: componentes conexos sobre as arestas RelationType=2 ───
  const parent = new Map<string, string>();
  const find = (node: string): string => {
    let root = node;
    while (parent.get(root) !== undefined && parent.get(root) !== root) {
      root = parent.get(root) as string;
    }
    return root;
  };
  const union = (a: string, b: string) => {
    if (!parent.has(a)) parent.set(a, a);
    if (!parent.has(b)) parent.set(b, b);
    const rootA = find(a);
    const rootB = find(b);
    if (rootA !== rootB) parent.set(rootA, rootB);
  };

  const dependsOn = new Map<string, string>();

  for (const item of catalog) {
    const pairedCode = item.parentCoverageCode ?? item.referenceCoverageCode;
    const pairedId = pairedCode ? idByCode.get(pairedCode) : undefined;
    if (!pairedId || pairedId === item.id) continue;

    if (item.relationType === COVERAGE_RELATION_EXCLUSIVE_WITH) {
      union(item.id, pairedId);
    } else if (item.relationType === COVERAGE_RELATION_DEPENDENT_ON) {
      dependsOn.set(item.id, pairedId);
    }
  }

  const membersByRoot = new Map<string, string[]>();
  for (const node of parent.keys()) {
    const root = find(node);
    const members = membersByRoot.get(root) ?? [];
    members.push(node);
    membersByRoot.set(root, members);
  }

  const exclusiveSiblings = new Map<string, string[]>();
  for (const members of membersByRoot.values()) {
    if (members.length < 2) continue;
    for (const id of members) {
      exclusiveSiblings.set(
        id,
        members.filter(other => other !== id)
      );
    }
  }

  // ─── Dependentes (inverso de dependsOn) ──────────────────────────────────
  const dependents = new Map<string, string[]>();
  for (const [childId, parentId] of dependsOn) {
    const list = dependents.get(parentId) ?? [];
    list.push(childId);
    dependents.set(parentId, list);
  }

  return { exclusiveSiblings, dependsOn, dependents };
}

/**
 * Fecho transitivo dos dependentes: desligar uma cobertura deve derrubar quem
 * depende dela e, em cascata, quem depende desses.
 */
export function collectDependentsDeep(
  id: string,
  relations: CoverageRelations
): string[] {
  const out: string[] = [];
  const queue = [...(relations.dependents.get(id) ?? [])];
  const seen = new Set<string>(queue);

  while (queue.length) {
    const current = queue.shift() as string;
    out.push(current);
    for (const next of relations.dependents.get(current) ?? []) {
      if (seen.has(next)) continue;
      seen.add(next);
      queue.push(next);
    }
  }

  return out;
}

export type CoverageRelationConflict = {
  coverageId: string;
  message: string;
};

/**
 * Conflitos de relação para o estado atual de seleção — fonte ÚNICA das
 * mensagens, consumida tanto pelo schema (bloqueia o submit) quanto pela tela
 * (acusa em tempo real). Manter as duas lendo daqui evita textos divergentes.
 *
 * A checagem é de GRUPO: com três coberturas do mesmo anel ligadas, cada uma
 * acusa as outras duas.
 */
export function findCoverageRelationConflicts(
  catalog: CapitalGlobalCoverageCatalogItem[],
  isEnabled: (id: string) => boolean
): CoverageRelationConflict[] {
  const relations = buildCoverageRelations(catalog);
  const titleById = new Map(catalog.map(item => [item.id, item.title]));
  const nameOf = (id: string) => titleById.get(id) ?? id;
  const conflicts: CoverageRelationConflict[] = [];

  for (const item of catalog) {
    if (!isEnabled(item.id)) continue;

    const clashing = (relations.exclusiveSiblings.get(item.id) ?? []).filter(
      isEnabled
    );
    if (clashing.length > 0) {
      conflicts.push({
        coverageId: item.id,
        message: `É proibida a contratação da cobertura ${item.title} junto com ${clashing
          .map(nameOf)
          .join(', ')}.`,
      });
      continue;
    }

    const requiredId = relations.dependsOn.get(item.id);
    if (requiredId && !isEnabled(requiredId)) {
      conflicts.push({
        coverageId: item.id,
        message: `A cobertura ${item.title} só pode ser contratada junto com ${nameOf(requiredId)}.`,
      });
    }
  }

  return conflicts;
}

/**
 * Pares assistência↔auxílio com acoplamento de NÍVEL mantidos em CÓDIGO
 * (código da assistência → código do auxílio pareado).
 *
 * O catálogo atual declara as Assistências Funeral SEM nenhuma relação com o
 * Auxílio equivalente: elas podem ser contratadas sozinhas (sem dependência) e
 * convivem com o auxílio contratado (sem exclusividade). O que permanece é o
 * vínculo de valor — com o auxílio pareado TAMBÉM contratado, os níveis
 * escolhidos devem ser IGUAIS. Como não há mais dado no catálogo que expresse
 * esse par, ele vive aqui.
 */
export const CAPITAL_COUPLED_AUXILIO_BY_ASSISTANCE_CODE: Record<
  string,
  string
> = {
  // Assistência Funeral Individual → Auxílio Funeral Individual
  '093_GLB_ASS_02': '9300025',
  // Assistência Funeral Familiar → Auxílio Funeral Familiar
  '093_GLB_ASS_03': '9300026',
  // Assistência Funeral Familiar Plus → Auxílio Funeral Familiar Ampliada
  '093_GLB_ASS_04': '9300027',
};

/**
 * Acoplamento de NÍVEL entre a cobertura e sua pareada (equivalente CG do
 * `linkedBenefitCode` do PME): quando as duas coberturas do par usam opções de
 * benefício (ex.: Auxílio Funeral R$ 7 mil ↔ Assistência Funeral), os valores
 * escolhidos devem ser IGUAIS.
 *
 * O par vem de DUAS fontes: o mapa em código
 * (`CAPITAL_COUPLED_AUXILIO_BY_ASSISTANCE_CODE`, para as assistências que o
 * catálogo não relaciona mais ao auxílio) e a dependência declarada
 * (`RelationType=1`, com pareada em `ParentCoverageCode`/`ReferenceCoverageCode`).
 *
 * Ancorado no CAPITAL — o `linkedBenefitCode` vem nulo na maior parte das
 * assistências do catálogo, e os níveis dos dois lados são os mesmos valores
 * em R$ — então a igualdade de capital expressa o mesmo vínculo com o dado que
 * temos no estado do form.
 *
 * Só dispara com AS DUAS contratadas e AMBOS os valores escolhidos (> 0) —
 * valor ainda não escolhido é responsabilidade do "Selecione uma opção". O
 * erro aparece nas DUAS linhas (mesmo padrão do PME), para o corretor entender
 * o que igualar. Nada é alterado automaticamente.
 */
export function findDependentCapitalConflicts(
  catalog: CapitalGlobalCoverageCatalogItem[],
  isEnabled: (id: string) => boolean,
  getCapital: (id: string) => number
): CoverageRelationConflict[] {
  const byCode = new Map(catalog.map(item => [item.code.trim(), item]));
  const conflicts: CoverageRelationConflict[] = [];

  for (const item of catalog) {
    const codeLevelPairedCode =
      CAPITAL_COUPLED_AUXILIO_BY_ASSISTANCE_CODE[item.code.trim()];
    const declaredPairedCode =
      item.relationType === COVERAGE_RELATION_DEPENDENT_ON
        ? (item.parentCoverageCode ?? item.referenceCoverageCode)
        : undefined;

    // O par em código só vale se o auxílio mapeado existir neste catálogo;
    // senão, cai para a dependência declarada (catálogos que ainda a trazem).
    const parent =
      (codeLevelPairedCode
        ? byCode.get(codeLevelPairedCode.trim())
        : undefined) ??
      (declaredPairedCode ? byCode.get(declaredPairedCode.trim()) : undefined);
    if (!parent || parent.id === item.id) continue;

    if (!hasBenefitCapitalOptions(item) || !hasBenefitCapitalOptions(parent)) {
      continue;
    }
    if (!isEnabled(item.id) || !isEnabled(parent.id)) continue;

    const itemCapital = getCapital(item.id);
    const parentCapital = getCapital(parent.id);
    if (!Number.isFinite(itemCapital) || itemCapital <= 0) continue;
    if (!Number.isFinite(parentCapital) || parentCapital <= 0) continue;
    if (itemCapital === parentCapital) continue;

    // O valor de referência é o do PAI (a dependente segue o pai). Usa o NOME
    // da opção quando ele existe para casar com o que o seletor mostra.
    const parentOptionName = parent.benefitOptions?.find(
      option => option.capital === parentCapital
    )?.name;
    const message = `As coberturas ${item.title} e ${parent.title} devem ter o mesmo valor (${
      parentOptionName ?? formatCurrency(parentCapital)
    }).`;

    conflicts.push({ coverageId: item.id, message });
    conflicts.push({ coverageId: parent.id, message });
  }

  return conflicts;
}
